import { voiceService } from '../services/voice-service';
import { updateAviCaptions, setEmotion, getAviSettings } from './avi';

declare const supabaseClient: any;
declare function showToast(msg: string): void;

// State
let isPodcastMode = false;
let isRecording = false;
let mediaRecorder: MediaRecorder | null = null;
let audioChunks: Blob[] = [];
let sessionStartTime: number | null = null;
let sessionTimer: any = null;
let currentSessionId: string | null = null;
let currentConversationId: string | null = null;
let selectedNiche = 'general';
let podcastHistory: { role: string; content: string }[] = [];

// Mode Switching
(window as any).switchAviMode = (mode: 'conversation' | 'podcast') => {
    isPodcastMode = mode === 'podcast';
    const controls = document.getElementById('podcastControls');
    const output = document.getElementById('podcastOutput');
    
    if (controls) controls.style.display = isPodcastMode ? 'block' : 'none';
    if (output) output.style.display = 'none';
    
    // Update mode buttons
    document.querySelectorAll('.mode-btn').forEach(btn => btn.classList.remove('active'));
    const clickedBtn = (event?.target as HTMLElement)?.closest?.('.mode-btn');
    if (clickedBtn) clickedBtn.classList.add('active');
    
    if (isPodcastMode) {
        updateAviCaptions('Podcast Mode - Select a niche and start recording');
    } else {
        updateAviCaptions('Conversation Mode');
    }
};

// Niche Selection
(window as any).onNicheChange = () => {
    const select = document.getElementById('podcastNiche') as HTMLSelectElement;
    selectedNiche = select?.value || 'general';
    showToast(`Niche: ${selectedNiche}`);
};

// Start Podcast Session
(window as any).startPodcastSession = async () => {
    if (!isPodcastMode) {
        showToast('Switch to Podcast mode first');
        return;
    }
    
    const user = (window as any).currentUser;
    if (!user || user.email === 'guest@ai-tools.com') {
        showToast('Please login to record podcasts');
        (window as any).showAuthOverlay();
        return;
    }
    
    isRecording = true;
    audioChunks = [];
    sessionStartTime = Date.now();
    podcastHistory = [];
    
    // Start timer
    sessionTimer = setInterval(updateSessionTimer, 1000);
    
    // Start audio recording
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        mediaRecorder = new MediaRecorder(stream);
        
        mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) audioChunks.push(e.data);
        };
        
        mediaRecorder.start();
    } catch (err) {
        console.error('Failed to start recording:', err);
        showToast('Microphone access denied');
        isRecording = false;
        return;
    }
    
    // Create conversation in Supabase
    try {
        const { data: conv } = await supabaseClient
            .from('conversations')
            .insert({
                user_id: user.id,
                title: `${selectedNiche} Podcast - ${new Date().toLocaleDateString()}`
            })
            .select()
            .single();
        
        currentConversationId = conv.id;
        
        const { data: session } = await supabaseClient
            .from('podcast_sessions')
            .insert({
                conversation_id: conv.id,
                user_id: user.id,
                niche: selectedNiche,
                status: 'recording'
            })
            .select()
            .single();
        
        currentSessionId = session.id;
    } catch (err) {
        console.error('Failed to create session:', err);
        showToast('Failed to start session');
        isRecording = false;
        return;
    }
    
    // Update UI
    document.getElementById('startPodcastBtn')?.setAttribute('disabled', 'true');
    document.getElementById('stopPodcastBtn')?.removeAttribute('disabled');
    document.getElementById('sessionStatus')!.textContent = 'Recording...';
    
    // AVI starts as podcast host
    setEmotion('happy');
    const openingQuestion = getPodcastOpeningQuestion(selectedNiche);
    updateAviCaptions(openingQuestion);
    voiceService.speak(openingQuestion, getAviSettings());
    
    // Store in history
    podcastHistory.push({ role: 'assistant', content: openingQuestion });
    
    showToast('Podcast recording started');
};

// Stop Podcast Session
(window as any).stopPodcastSession = async () => {
    if (!isRecording) return;
    
    isRecording = false;
    
    // Stop recording
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
        mediaRecorder.stop();
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
    
    // Stop timer
    if (sessionTimer) clearInterval(sessionTimer);
    
    const duration = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    
    // Update UI
    document.getElementById('startPodcastBtn')?.removeAttribute('disabled');
    document.getElementById('stopPodcastBtn')?.setAttribute('disabled', 'true');
    document.getElementById('sessionStatus')!.textContent = 'Processing...';
    
    setEmotion('thinking');
    updateAviCaptions('Generating episode summary...');
    
    try {
        // Upload audio to Supabase Storage
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const fileName = `${currentSessionId}/recording.webm`;
        
        const { data: uploadData, error: uploadError } = await supabaseClient.storage
            .from('podcast-recordings')
            .upload(fileName, audioBlob);
        
        let audioUrl = null;
        if (!uploadError) {
            const { data: urlData } = supabaseClient.storage
                .from('podcast-recordings')
                .getPublicUrl(fileName);
            audioUrl = urlData.publicUrl;
        }
        
        // Update session
        await supabaseClient
            .from('podcast_sessions')
            .update({
                status: 'processing',
                audio_url: audioUrl,
                duration_seconds: duration,
                completed_at: new Date().toISOString()
            })
            .eq('id', currentSessionId);
        
        // Generate podcast outputs using LLM
        await generatePodcastOutputs();
        
        // Update user preferences
        await updateUserPreferences();
        
        showToast('Episode generated successfully!');
    } catch (err) {
        console.error('Failed to process session:', err);
        showToast('Error processing episode');
        setEmotion('sad');
    }
};

// Generate Podcast Outputs
async function generatePodcastOutputs() {
    try {
        const { fetchAiToolsMetaModel } = await import('./ai-helper');
        
        const conversationText = podcastHistory
            .map(msg => `${msg.role === 'user' ? 'Guest' : 'Host'}: ${msg.content}`)
            .join('\n');
        
        const systemPrompt = `You are an expert podcast producer. Analyze this conversation and generate structured outputs.

NICHE: ${selectedNiche}

CONVERSATION:
${conversationText}

Generate JSON output with:
{
  "title": "Catchy episode title",
  "structure": ["Intro", "Segment 1", "Segment 2", "Conclusion"],
  "insights": ["Key insight 1", "Key insight 2", "Key insight 3"],
  "summary": "2-3 paragraph episode summary",
  "next_episode": "Suggested topic for next episode",
  "social_clips": ["Clip idea 1", "Clip idea 2"]
}`;
        
        const response = await fetchAiToolsMetaModel('Generate podcast outputs', [
            { role: 'system', content: systemPrompt }
        ]);
        
        // Parse JSON from response
        let outputs;
        try {
            const jsonMatch = response.match(/\{[\s\S]*\}/);
            outputs = jsonMatch ? JSON.parse(jsonMatch[0]) : {};
        } catch {
            outputs = {
                title: 'Podcast Episode',
                insights: ['Conversation completed'],
                structure: ['Discussion'],
                summary: response,
                next_episode: 'Continue the conversation',
                social_clips: []
            };
        }
        
        // Save to database
        await supabaseClient
            .from('podcast_outputs')
            .insert({
                session_id: currentSessionId,
                generated_title: outputs.title,
                episode_structure: JSON.stringify(outputs.structure),
                key_insights: JSON.stringify(outputs.insights),
                summary: outputs.summary,
                suggested_next_episode: outputs.next_episode,
                social_clips: JSON.stringify(outputs.social_clips || [])
            });
        
        // Update session status
        await supabaseClient
            .from('podcast_sessions')
            .update({ status: 'completed', title: outputs.title })
            .eq('id', currentSessionId);
        
        // Display outputs
        displayPodcastOutputs(outputs);
        
        // Update AVI
        setEmotion('happy');
        updateAviCaptions(`Episode complete! Title: ${outputs.title}`);
        voiceService.speak(`Great episode! I've generated the summary and insights. The title is ${outputs.title}`, getAviSettings());
        
    } catch (err) {
        console.error('Failed to generate outputs:', err);
        throw err;
    }
}

// Display Podcast Outputs
function displayPodcastOutputs(outputs: any) {
    const outputPanel = document.getElementById('podcastOutput');
    if (!outputPanel) return;
    
    outputPanel.style.display = 'block';
    
    document.getElementById('outputTitle')!.textContent = outputs.title || 'N/A';
    document.getElementById('outputNextEpisode')!.textContent = outputs.next_episode || 'N/A';
    
    const insightsList = document.getElementById('outputInsights')!;
    insightsList.innerHTML = '';
    (outputs.insights || []).forEach((insight: string) => {
        const li = document.createElement('li');
        li.textContent = insight;
        insightsList.appendChild(li);
    });
    
    const structureDiv = document.getElementById('outputStructure')!;
    structureDiv.innerHTML = '';
    (outputs.structure || []).forEach((segment: string, i: number) => {
        const p = document.createElement('p');
        p.textContent = `${i + 1}. ${segment}`;
        structureDiv.appendChild(p);
    });
}

// Update Session Timer
function updateSessionTimer() {
    if (!sessionStartTime) return;
    
    const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
    const minutes = Math.floor(elapsed / 60).toString().padStart(2, '0');
    const seconds = (elapsed % 60).toString().padStart(2, '0');
    
    const timerEl = document.getElementById('sessionTimer');
    if (timerEl) timerEl.textContent = `${minutes}:${seconds}`;
}

// Get Podcast Opening Question
function getPodcastOpeningQuestion(niche: string): string {
    const questions: Record<string, string> = {
        'general': "What's on your mind today? Let's dive into a great conversation.",
        'entrepreneurship': "What's one problem you've been thinking about solving recently?",
        'technology': "What's the most exciting tech development you've seen lately?",
        'health': "What aspect of health or fitness are you most focused on right now?",
        'philosophy': "What's a question that's been on your mind recently?",
        'education': "What's something you've learned recently that changed your perspective?",
        'entertainment': "What's the best movie, show, or book you've experienced recently?",
        'current-events': "What current event or news story has been on your mind?"
    };
    
    return questions[niche] || questions['general'];
}

// Update User Preferences
async function updateUserPreferences() {
    const user = (window as any).currentUser;
    if (!user) return;
    
    try {
        // Check if preferences exist
        const { data: existing } = await supabaseClient
            .from('user_podcast_preferences')
            .select('*')
            .eq('user_id', user.id)
            .single();
        
        if (existing) {
            // Update existing
            const niches = existing.preferred_niches || [];
            if (!niches.includes(selectedNiche)) {
                niches.push(selectedNiche);
            }
            
            await supabaseClient
                .from('user_podcast_preferences')
                .update({
                    preferred_niches: niches,
                    total_sessions: (existing.total_sessions || 0) + 1,
                    last_session_at: new Date().toISOString(),
                    updated_at: new Date().toISOString()
                })
                .eq('user_id', user.id);
        } else {
            // Create new
            await supabaseClient
                .from('user_podcast_preferences')
                .insert({
                    user_id: user.id,
                    preferred_niches: [selectedNiche],
                    total_sessions: 1,
                    last_session_at: new Date().toISOString()
                });
        }
    } catch (err) {
        console.error('Failed to update preferences:', err);
    }
}

// Download Podcast
(window as any).downloadPodcast = () => {
    if (audioChunks.length === 0) {
        showToast('No recording available');
        return;
    }
    
    const blob = new Blob(audioChunks, { type: 'audio/webm' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `podcast-${currentSessionId}.webm`;
    a.click();
    URL.revokeObjectURL(url);
};

// Override AVI listening for podcast mode
export function handlePodcastUserInput(text: string) {
    if (!isPodcastMode || !isRecording) return;
    
    // Store user input
    podcastHistory.push({ role: 'user', content: text });
    
    // Generate AI host response
    generateHostResponse(text);
}

// Generate AI Host Response
async function generateHostResponse(userText: string) {
    try {
        const { fetchAiToolsMetaModel } = await import('./ai-helper');
        
        const systemPrompt = `You are an expert podcast host specializing in ${selectedNiche}. 

YOUR ROLE:
- Ask intelligent, context-aware follow-up questions
- Deepen the conversation and extract insights
- Challenge ideas constructively
- Share relevant perspectives
- Keep responses conversational and concise (2-4 sentences)

STYLE:
- Professional yet engaging
- Curious and thoughtful
- Adapt to guest's energy level

Previous conversation context:
${podcastHistory.slice(-6).map(m => `${m.role}: ${m.content}`).join('\n')}

Guest just said: "${userText}"

Respond with a follow-up that increases depth, clarity, or insight.`;
        
        const response = await fetchAiToolsMetaModel(userText, [
            { role: 'system', content: systemPrompt },
            ...podcastHistory.slice(-10)
        ]);
        
        // Store and speak
        podcastHistory.push({ role: 'assistant', content: response });
        
        updateAviCaptions(response);
        setEmotion('thinking');
        
        setTimeout(() => {
            setEmotion('happy');
            voiceService.speak(response, getAviSettings());
        }, 500);
        
    } catch (err) {
        console.error('Failed to generate host response:', err);
        updateAviCaptions('Interesting point! Tell me more about that.');
    }
}

export { isPodcastMode, isRecording, selectedNiche };
